import { useCallback, useEffect, useState } from "react";
import { theme, card, emptyFrame, btnPrimary, inputStyle, type as text } from "../theme";
import { api, apiErrorMessage, ApiError } from "../api/client";
import { fmtDate } from "../lib/format";
import { PortalLink } from "./PortalLink";
import { TIER_NAMES } from "./TierLockModal";
import { secondaryBtn, dangerBtn } from "../pages/resilience/shared";

// The cross-supplier assurance cards on the TPRM page's Monitoring tab
// (docs/TPRM-SUPPLIER-ASSURANCE.md):
//   * automatic chasing, weekly public-record checks and the renewal questionnaire (GET / PATCH /tprm/settings);
//   * shared fourth parties — providers several suppliers rely on (GET /tprm/fourth-parties);
//   * supplier access to Microsoft 365 — guest accounts by supplier (GET /tprm/guest-access);
//   * vendor ratings — the organisation's own ratings account (/supplier-ratings, L3).

const smallBtn: React.CSSProperties = { ...secondaryBtn, padding: "5px 12px", fontSize: 12 };
const th: React.CSSProperties = { padding: "8px 12px", textAlign: "left", fontWeight: 600, fontSize: 12, color: theme.color.muted, whiteSpace: "nowrap" };
const td: React.CSSProperties = { padding: "8px 12px", fontSize: 12, color: theme.color.text, verticalAlign: "top" };

/** An on / off switch drawn square, as the rest of the paper. */
function Switch({ on, onChange, disabled, label }: { on: boolean; onChange: (v: boolean) => void; disabled?: boolean; label: string }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} disabled={disabled} onClick={() => onChange(!on)}
      style={{ display: "inline-block", position: "relative", width: 36, height: 20, borderRadius: 3, border: "none", padding: 0, flexShrink: 0,
        background: on ? "var(--c-accent)" : "var(--c-border)", cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.6 : 1 }}>
      <span style={{ position: "absolute", top: 3, left: on ? 19 : 3, width: 14, height: 14, borderRadius: 2, background: "var(--c-panel)", transition: "left 0.2s" }} />
    </button>
  );
}

// ── Automatic chasing and weekly checks ─────────────────────────────────────

interface TprmSettings {
  autoChase: boolean;
  domainChecks: boolean;
  renewalAutoSend: boolean;
  chase: { afterDays: number; intervalDays: number; maxReminders: number };
  renewal: { reassessDays: number };
}
type SettingKey = "autoChase" | "domainChecks" | "renewalAutoSend";

export function SupplierAutomationCard({ canWrite, onChanged }: { canWrite: boolean; onChanged?: () => void }) {
  const [settings, setSettings] = useState<TprmSettings | null>(null);
  const [saving, setSaving] = useState<SettingKey | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<TprmSettings>("/tprm/settings").then(setSettings).catch((e) => setError(apiErrorMessage(e, "Could not load the supplier settings.")));
  }, []);

  async function change(key: SettingKey, value: boolean) {
    if (!settings) return;
    if (key === "autoChase" && value && !confirm("Switch on automatic chasing? The platform will email suppliers' contacts on your organisation's behalf.")) return;
    if (key === "renewalAutoSend" && value && !confirm("Send renewal questionnaires automatically? The platform will email suppliers' contacts on your organisation's behalf.")) return;
    setSaving(key);
    setError(null);
    try {
      const r = await api.patch<{ autoChase: boolean; domainChecks: boolean; renewalAutoSend: boolean }>("/tprm/settings", { [key]: value });
      setSettings({ ...settings, autoChase: r.autoChase, domainChecks: r.domainChecks, renewalAutoSend: r.renewalAutoSend });
      onChanged?.();
    } catch (e) {
      setError(apiErrorMessage(e, "Could not save the setting."));
    } finally {
      setSaving(null);
    }
  }

  if (!settings) return error ? <div role="alert" style={{ ...card, ...text.hint, color: theme.color.dangerText }}>{error}</div> : null;
  const c = settings.chase;
  return (
    <div style={{ ...card }}>
      <div style={{ ...text.sectionTitle }}>Automatic monitoring</div>
      <div style={{ display: "grid", gap: 14 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
          <Switch on={settings.autoChase} onChange={(v) => change("autoChase", v)} disabled={!canWrite || saving !== null} label="Chase unanswered questionnaires automatically" />
          <div>
            <div style={{ ...text.body, fontWeight: 600 }}>Chase unanswered questionnaires automatically</div>
            <div style={{ ...text.hint }}>
              Off by default. When on, the platform emails the supplier's contact a reminder once a questionnaire has waited {c.afterDays} days, then again every {c.intervalDays} days, up to {c.maxReminders} reminder{c.maxReminders === 1 ? "" : "s"}, never at weekends.
              Each reminder sends a new link and the old one stops working. After the last reminder, or where the supplier has no contact address, it stops writing to the supplier and tells the people who manage suppliers.
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
          <Switch on={settings.domainChecks} onChange={(v) => change("domainChecks", v)} disabled={!canWrite || saving !== null} label="Check suppliers' public records weekly" />
          <div>
            <div style={{ ...text.body, fontWeight: 600 }}>Check suppliers' public records weekly</div>
            <div style={{ ...text.hint }}>
              Reads each supplier's public DNS records and website (SPF, DMARC, TLS, HSTS, security.txt) and its published sub-processor page where one is recorded. Public records only: no supplier system is tested.
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
          <Switch on={settings.renewalAutoSend} onChange={(v) => change("renewalAutoSend", v)} disabled={!canWrite || saving !== null} label="Send the renewal questionnaire automatically" />
          <div>
            <div style={{ ...text.body, fontWeight: 600 }}>Send the renewal questionnaire automatically</div>
            <div style={{ ...text.hint }}>
              Off by default. {settings.renewal.reassessDays} days before an agreement's notice deadline the platform proposes re-assessing the supplier and refreshes its public-record checks.
              When this is on, it also emails the supplier's contact a fresh questionnaire from its tier set, once per term, never at weekends, unless one is already out. When off, a person sends it from the supplier's Contracts tab.
            </div>
          </div>
        </div>
      </div>
      {!canWrite && <div style={{ ...text.meta, marginTop: 8 }}>Only people who manage suppliers can change these.</div>}
      {error && <div role="alert" style={{ ...text.hint, color: theme.color.dangerText, marginTop: 8 }}>{error}</div>}
    </div>
  );
}

// ── Shared fourth parties ───────────────────────────────────────────────────

interface FourthPartyConcentration {
  normalisedName: string;
  name: string;
  suppliers: Array<{ id: string; name: string; tier: string }>;
  importantSuppliers: number;
  inferredOnly: boolean;
}

export function SharedFourthPartiesCard({ onOpen }: { onOpen: (supplierId: string) => void }) {
  const [rows, setRows] = useState<FourthPartyConcentration[] | null>(null);
  const [total, setTotal] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ concentration: FourthPartyConcentration[]; rows: unknown[] }>("/tprm/fourth-parties")
      .then((d) => { setRows(d.concentration); setTotal(d.rows.length); })
      .catch((e) => setError(apiErrorMessage(e, "Could not load suppliers' own suppliers.")));
  }, []);

  if (error) return <div role="alert" style={{ ...card, ...text.hint, color: theme.color.dangerText }}>{error}</div>;
  if (!rows) return null;
  const shown = showAll ? rows : rows.slice(0, 6);
  return (
    <div style={{ ...card }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
        <span style={{ ...text.sectionTitle, margin: 0 }}>Shared fourth parties</span>
        <span style={text.hint}>{rows.length} provider{rows.length === 1 ? "" : "s"} relied on by more than one supplier · {total} recorded in all</span>
      </div>
      <p style={{ ...text.hint, margin: "4px 0 10px" }}>
        Where several of your suppliers rely on the same provider, one failure there reaches all of them. Open a supplier to review its own suppliers on its Assurance tab.
      </p>
      {rows.length === 0 ? (
        <div style={{ ...emptyFrame, ...text.hint }}>No provider is shared by two or more of your suppliers.</div>
      ) : (
        <div style={{ display: "grid", gap: 0 }}>
          {shown.map((r) => (
            <div key={r.normalisedName} style={{ padding: "8px 0", borderTop: `1px solid ${theme.color.border}` }}>
              <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
                <span style={text.itemTitle}>{r.name}</span>
                <span style={text.hint}>{r.suppliers.length} suppliers{r.importantSuppliers ? `, ${r.importantSuppliers} high or critical` : ""}</span>
                {r.inferredOnly && <span style={{ fontSize: 11, fontWeight: 600, padding: "1px 7px", borderRadius: 2, background: theme.color.panelAlt, color: theme.color.muted }}>inferred from DNS only</span>}
              </div>
              <div style={{ ...text.hint, color: theme.color.muted }}>
                {r.suppliers.map((s, i) => (
                  <span key={s.id}>{i > 0 ? ", " : ""}
                    <button type="button" onClick={() => onOpen(s.id)} style={{ padding: 0, border: "none", background: "none", font: "inherit", color: "var(--c-accentText)", cursor: "pointer" }}>{s.name}</button>
                    {s.tier === "critical" || s.tier === "high" ? ` (${s.tier})` : ""}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {rows.length > 6 && (
        <button type="button" onClick={() => setShowAll((v) => !v)} style={{ ...smallBtn, marginTop: 8 }}>{showAll ? "Show fewer" : `Show all ${rows.length}`}</button>
      )}
    </div>
  );
}

// ── Supplier access to Microsoft 365 ───────────────────────────────────────

interface GuestAccess {
  connected: boolean;
  enabled: boolean;
  lastSync: string | null;
  error: string | null;
  summary: {
    total: number;
    enabled: number;
    bySupplier: Array<{ supplierId: string; supplierName: string; supplierStatus: string; supplierTier: string; guests: number; enabled: number; offboarded: number; dormant: number; noMfa: number }>;
    unknownDomains: Array<{ domain: string; guests: number }>;
  };
}

export function SupplierGuestAccessCard({ onOpen }: { onOpen: (supplierId: string) => void }) {
  const [data, setData] = useState<GuestAccess | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<GuestAccess>("/tprm/guest-access").then(setData).catch((e) => setError(apiErrorMessage(e, "Could not load supplier guest accounts.")));
  }, []);

  if (error) return <div role="alert" style={{ ...card, ...text.hint, color: theme.color.dangerText }}>{error}</div>;
  if (!data) return null;
  const s = data.summary;
  const warn = (n: number) => (n ? { color: theme.color.dangerText, fontWeight: 600 } : { color: theme.color.muted });
  return (
    <div style={{ ...card }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
        <span style={{ ...text.sectionTitle, margin: 0 }}>Supplier access to Microsoft 365</span>
        {data.connected && data.enabled && <span style={text.hint}>{s.total} guest account{s.total === 1 ? "" : "s"}, {s.enabled} enabled{data.lastSync ? ` · read ${fmtDate(data.lastSync)}` : ""}</span>}
      </div>
      <p style={{ ...text.hint, margin: "4px 0 10px" }}>
        Guest accounts suppliers' people hold in your tenant, matched to suppliers by their home domain: accounts of an offboarded supplier, accounts no one has used, and sign-ins without multi-factor authentication.
      </p>
      {!data.connected ? (
        <div style={{ ...emptyFrame, ...text.hint }}>
          Microsoft 365 Security must be connected, with the <code style={text.mono}>User.Read.All</code> and <code style={text.mono}>AuditLog.Read.All</code> application permissions. <PortalLink to="/connections" style={{ color: "var(--c-accentText)" }}>Open Connections</PortalLink>
        </div>
      ) : !data.enabled ? (
        <div style={{ ...emptyFrame, ...text.hint }}>
          Switch on Supplier guest accounts on the Microsoft 365 Security connection. It needs <code style={text.mono}>User.Read.All</code> and <code style={text.mono}>AuditLog.Read.All</code>. <PortalLink to="/connections" style={{ color: "var(--c-accentText)" }}>Open Connections</PortalLink>
        </div>
      ) : (
        <>
          {data.error && <div role="alert" style={{ ...text.hint, color: theme.color.warningText, marginBottom: 8 }}>{data.error}</div>}
          {s.bySupplier.length === 0 ? (
            <div style={{ ...emptyFrame, ...text.hint }}>No guest account in your tenant matches a supplier's domain.</div>
          ) : (
            <div style={{ border: `1px solid ${theme.color.border}`, borderRadius: 3, overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr style={{ borderBottom: "1px solid var(--c-line)" }}>
                  {["Supplier", "Guests", "Enabled", "Supplier offboarded", "Dormant", "Without MFA"].map((h) => <th key={h} style={th}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {s.bySupplier.map((r, i) => (
                    <tr key={r.supplierId} style={{ borderTop: i ? `1px solid ${theme.color.border}` : "none" }}>
                      <td style={td}>
                        <button type="button" onClick={() => onOpen(r.supplierId)} style={{ padding: 0, border: "none", background: "none", font: "inherit", fontWeight: 600, color: theme.color.heading, cursor: "pointer", textAlign: "left" }}>{r.supplierName}</button>
                        <div style={{ ...text.meta, textTransform: "capitalize" }}>{r.supplierStatus.replace(/_/g, " ")} · {r.supplierTier}</div>
                      </td>
                      <td style={td}>{r.guests}</td>
                      <td style={td}>{r.enabled}</td>
                      <td style={{ ...td, ...warn(r.offboarded) }}>{r.offboarded}</td>
                      <td style={{ ...td, ...(r.dormant ? { color: theme.color.warningText, fontWeight: 600 } : { color: theme.color.muted }) }}>{r.dormant}</td>
                      <td style={{ ...td, ...warn(r.noMfa) }}>{r.noMfa}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {s.unknownDomains.length > 0 && (
            <div style={{ ...text.hint, marginTop: 10 }}>
              <strong style={{ color: theme.color.warningText }}>Guests from domains no supplier is recorded for:</strong>{" "}
              {s.unknownDomains.slice(0, 12).map((d) => `${d.domain} (${d.guests})`).join(", ")}{s.unknownDomains.length > 12 ? `, and ${s.unknownDomains.length - 12} more` : ""}.
              {" "}Add the supplier, or set its domain on its Assurance tab.
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Vendor ratings ──────────────────────────────────────────────────────────

interface RatingsVendor { id: string; label: string; host: string; scaleMin: number; scaleMax: number; note: string }
interface RatingsStatus {
  connected: boolean;
  vendor: string | null;
  connectedAt: string | null;
  lastSyncAt: string | null;
  lastError: string | null;
  readings: number;
  vendors: RatingsVendor[];
}

export function VendorRatingsCard({ canAdmin }: { canAdmin: boolean }) {
  const [status, setStatus] = useState<RatingsStatus | null>(null);
  const [locked, setLocked] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [vendor, setVendor] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [busy, setBusy] = useState<"connect" | "sync" | "disconnect" | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const s = await api.get<RatingsStatus>("/supplier-ratings/status");
      setStatus(s);
      setVendor((v) => v || s.vendors[0]?.id || "");
    } catch (e) {
      if (e instanceof ApiError && e.status === 402 && e.code === "tier_locked") {
        setLocked((e.payload as { requiredLevel?: number })?.requiredLevel ?? 3);
      } else {
        setError(apiErrorMessage(e, "Could not load the vendor ratings connection."));
      }
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function connect(e: React.FormEvent) {
    e.preventDefault();
    if (!vendor || !apiKey.trim()) return;
    setBusy("connect");
    setError(null);
    setNotice(null);
    try {
      await api.post("/supplier-ratings/connect", { vendor, apiKey: apiKey.trim() });
      setNotice("Connected. Ratings are read for suppliers with a known domain at the next sync.");
      await load();
    } catch (err) {
      setError(apiErrorMessage(err, "The key could not be checked with the vendor."));
    } finally {
      // The key is never kept or shown again, whatever the outcome.
      setApiKey("");
      setBusy(null);
    }
  }

  async function sync() {
    setBusy("sync");
    setError(null);
    setNotice(null);
    try {
      await api.post("/supplier-ratings/sync", {});
      setNotice("Sync queued. New readings appear on each supplier's Assurance tab once it has run.");
    } catch (err) {
      setError(apiErrorMessage(err, "The sync could not be queued."));
    } finally {
      setBusy(null);
    }
  }

  async function disconnect() {
    if (!confirm("Disconnect the vendor ratings account? The key is deleted; readings already taken stay on each supplier.")) return;
    setBusy("disconnect");
    setError(null);
    setNotice(null);
    try {
      await api.del("/supplier-ratings/connect");
      await load();
    } catch (err) {
      setError(apiErrorMessage(err, "Could not disconnect."));
    } finally {
      setBusy(null);
    }
  }

  const current = status?.vendors.find((v) => v.id === (status.connected ? status.vendor : vendor));

  return (
    <div style={{ ...card }}>
      <div style={{ ...text.sectionTitle }}>Vendor ratings</div>
      <p style={{ ...text.hint, margin: "0 0 10px" }}>
        Uses your organisation's own account with a security ratings vendor. Only your suppliers' domains are sent, to the vendor's API host, and the vendor's score comes back to sit beside the records on each supplier's Assurance tab.
        Where the vendor holds its data, and on what terms, is between your organisation and the vendor.
      </p>

      {locked != null ? (
        <div style={{ ...emptyFrame, ...text.hint }}>
          Vendor ratings need the {TIER_NAMES[locked] ?? `level ${locked}`} service tier or above. Ask your consultant, or see <PortalLink to="/my-services" style={{ color: "var(--c-accentText)" }}>My Services</PortalLink>.
        </div>
      ) : !status ? (
        error ? <div role="alert" style={{ ...text.hint, color: theme.color.dangerText }}>{error}</div> : <div style={text.hint}>Loading…</div>
      ) : status.connected ? (
        <>
          <div style={{ ...text.body }}>
            Connected to <strong>{current?.label ?? status.vendor}</strong>{current ? <span style={text.hint}> ({current.host})</span> : null}
          </div>
          <div style={{ ...text.hint }}>
            {status.connectedAt ? `Connected ${fmtDate(status.connectedAt)}` : ""}
            {status.lastSyncAt ? ` · last read ${fmtDate(status.lastSyncAt)}` : " · not read yet"}
            {` · ${status.readings} reading${status.readings === 1 ? "" : "s"} held`}
          </div>
          {status.lastError && <div role="alert" style={{ ...text.hint, color: theme.color.warningText, marginTop: 4 }}>{status.lastError}</div>}
          {canAdmin && (
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <button type="button" onClick={sync} disabled={busy !== null} style={{ ...smallBtn, opacity: busy === "sync" ? 0.6 : 1 }}>{busy === "sync" ? "Queuing…" : "Sync now"}</button>
              <button type="button" onClick={disconnect} disabled={busy !== null} style={{ ...dangerBtn, padding: "5px 12px", fontSize: 12 }}>Disconnect</button>
            </div>
          )}
        </>
      ) : canAdmin ? (
        <form onSubmit={connect} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 10, alignItems: "end" }}>
          <label>
            <span style={text.label}>Vendor</span>
            <select value={vendor} onChange={(e) => setVendor(e.target.value)} style={inputStyle}>
              {status.vendors.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
            </select>
          </label>
          <label>
            <span style={text.label}>API key</span>
            <input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} autoComplete="off" spellCheck={false} placeholder="Your organisation's key" style={inputStyle} />
          </label>
          {current && (
            <div style={{ ...text.meta, gridColumn: "1 / -1" }}>
              Calls go to {current.host} only. {current.note || `Scores run from ${current.scaleMin} to ${current.scaleMax}.`}
            </div>
          )}
          <div style={{ gridColumn: "1 / -1" }}>
            <button type="submit" disabled={busy !== null || !apiKey.trim() || !vendor} style={{ ...btnPrimary, padding: "6px 14px", fontSize: 12, opacity: busy !== null || !apiKey.trim() ? 0.6 : 1 }}>
              {busy === "connect" ? "Checking the key…" : "Connect"}
            </button>
            <span style={{ ...text.meta, marginLeft: 10 }}>The key is tested with the vendor first, stored encrypted and never shown again.</span>
          </div>
        </form>
      ) : (
        <div style={{ ...emptyFrame, ...text.hint }}>No ratings account is connected. Someone who manages your integrations can connect one here.</div>
      )}

      {status && error && <div role="alert" style={{ ...text.hint, color: theme.color.dangerText, marginTop: 8 }}>{error}</div>}
      {notice && <div role="status" style={{ ...text.hint, color: theme.color.successText, marginTop: 8 }}>{notice}</div>}
    </div>
  );
}
